
import { gsap } from "gsap";

export default function toggleCommonAnimation(el, forcedDelay = 0) {
    if (!el) return;

    const element = typeof el === "string" ? document.querySelector(el) : el;
    if (!element) return;

    const anim = element.getAttribute("data-animation");
    const animDelay = forcedDelay;

    let animStart = {};
    let animEnd = {};

    element.style.visibility = "visible";
    element.style.willChange = "opacity, transform";

    if (anim === "stagger-up") {
        const children = Array.from(element.children || []);
        if (!children.length) return;

        gsap.set(element, { opacity: 1, visibility: "visible" });
        gsap.set(children, { opacity: 0, y: 30 });
        return gsap.to(children, {
            duration: 0.3,
            delay: animDelay,
            opacity: 1,
            y: 0,
            stagger: 0.3,
            ease: "power1.out",
            onComplete: () => {
                element.classList.add("animation-complete");
            }
        });
    }

    if (anim === "reveal-rl" || anim === "reveal-lr") {
        const revealFromRight = anim === "reveal-rl";
        const media = element.querySelector("img, picture, video, canvas, svg") || element.firstElementChild || element;
        const overlayColor = element.getAttribute("data-reveal-color") || "var(--primary-400)";
        const overlayInX = revealFromRight ? "100%" : "-100%";
        const overlayOutX = revealFromRight ? "-100%" : "100%";
        const mediaX = revealFromRight ? -10 : 10;
        const existingOverlay = element.querySelector(".js-reveal-overlay");
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement("span");
        overlay.className = "js-reveal-overlay";
        overlay.setAttribute("aria-hidden", "true");
        overlay.style.position = "absolute";
        overlay.style.inset = "0";
        overlay.style.backgroundColor = overlayColor;
        overlay.style.zIndex = "2";
        overlay.style.pointerEvents = "none";
        element.appendChild(overlay);

        gsap.set(element, {
            opacity: 1,
            visibility: "visible",
            overflow: "hidden"
        });

        // L'overlay all'inizio è fuori quadro: se il media è già opaco, l'immagine si vede
        // prima del layer colorato. Resta nascosta finché l'overlay non inizia a scostarsi (reveal).
        const revealStart = 0.22;
        if (media && media !== element) {
            gsap.set(media, {
                opacity: 0,
                xPercent: mediaX,
                scale: 1.05,
                willChange: "transform, opacity"
            });
        }
        gsap.set(overlay, { x: overlayInX });

        const tl = gsap.timeline({
            delay: animDelay + 0.05,
            defaults: { ease: "power1.out" },
            onComplete: () => {
                overlay.remove();
                element.classList.add("animation-complete");
            }
        });

        tl.to(overlay, { duration: 0.35, x: "0%" }, 0);
        tl.to(overlay, { duration: 0.45, x: overlayOutX }, revealStart);

        if (media && media !== element) {
            tl.to(
                media,
                { duration: 0.7, opacity: 1, xPercent: 0, scale: 1 },
                revealStart
            );
        }

        return tl;
    }

    // Crafto demo-recruitment: data-fancy-text (Splitting + anime → char span + GSAP stagger).
    if (anim === "hero-fancy-text") {
        const defaults = {
            scale: [1.1, 1],
            rotateX: [20, 0],
            opacity: [0, 1],
            translateX: [-20, 0],
            translateY: [20, 0],
            delay: 0,
            speed: 40,
            easing: "easeOutQuad",
        };
        let opts = { ...defaults };
        const json = element.getAttribute("data-fancy-text");
        if (json) {
            try {
                opts = { ...defaults, ...JSON.parse(json) };
            } catch (e) {
                opts = { ...defaults };
            }
        }

        const easeMap = {
            easeOutQuad: "power2.out",
            easeInOutQuad: "power2.inOut",
            easeOutCubic: "power3.out",
            easeInOutCubic: "power3.inOut",
            linear: "none",
        };
        const ease = easeMap[opts.easing] || "power2.out";

        const raw = (element.textContent || "").trim();
        if (!raw) {
            return;
        }

        const words = raw.split(/\s+/).filter(Boolean);
        element.textContent = "";
        const charEls = [];
        words.forEach((word, wIndex) => {
            const wordWrap = document.createElement("span");
            wordWrap.className = "hero-fancy-word";
            wordWrap.setAttribute("aria-hidden", "true");
            for (const ch of Array.from(word)) {
                const span = document.createElement("span");
                span.className = "hero-fancy-char";
                span.style.display = "inline-block";
                span.textContent = ch;
                wordWrap.appendChild(span);
                charEls.push(span);
            }
            element.appendChild(wordWrap);
            if (wIndex < words.length - 1) {
                element.appendChild(document.createTextNode(" "));
            }
        });

        element.style.opacity = "1";
        element.style.visibility = "visible";
        element.style.willChange = "auto";

        const [s0, s1] = opts.scale;
        const [rx0, rx1] = opts.rotateX;
        const [o0, o1] = opts.opacity;
        const [x0, x1] = opts.translateX;
        const [y0, y1] = opts.translateY;
        const staggerSec = (opts.speed || 40) / 1000;
        const baseDelay = (opts.delay || 0) / 1000 + animDelay;

        gsap.set(charEls, {
            opacity: o0,
            scale: s0,
            rotationX: rx0,
            x: x0,
            y: y0,
            transformOrigin: "50% 85%",
            force3D: true,
        });

        return gsap.to(charEls, {
            opacity: o1,
            scale: s1,
            rotationX: rx1,
            x: x1,
            y: y1,
            duration: 0.55,
            stagger: {
                each: staggerSec,
                from: "start",
            },
            delay: baseDelay,
            ease,
            onComplete: () => {
                element.classList.add("animation-complete");
            },
        });
    }

    switch (anim) {
        case "fade-up":
            animStart = { opacity: 0, y: 40 };
            animEnd = { duration: 0.5, delay: animDelay, opacity: 1, y: 0, ease: "power1.out" };
            break;

        case "fade-down":
            animStart = { opacity: 0, y: -40 };
            animEnd = { duration: 0.5, delay: animDelay, opacity: 1, y: 0, ease: "power1.out" };
            break;

        case "fade-left":
            animStart = { opacity: 0, x: 40 };
            animEnd = { duration: 0.5, delay: animDelay, opacity: 1, x: 0, ease: "power1.out" };
            break;

        case "fade-right":
            animStart = { opacity: 0, x: -40 };
            animEnd = { duration: 0.5, delay: animDelay, opacity: 1, x: 0, ease: "power1.out" };
            break;

        case "zoom-in":
            animStart = { opacity: 0, scale: 0.85 };
            animEnd = { duration: 0.5, delay: animDelay, opacity: 1, scale: 1, ease: "power1.out" };
            break;

        // Hero typography entrance animation (recreated from Crafto recruitment demo).
        // It relies on CSS for the 3D transform, and GSAP for the opacity timing.
        case "hero-typography":
            animStart = { opacity: 0 };
            animEnd = { duration: 0.9, delay: animDelay, opacity: 1, ease: "circ.out" };
            break;

        default:
            animStart = { opacity: 0 };
            animEnd = { duration: 0.5, delay: animDelay, opacity: 1, ease: "power1.out" };
    }

    gsap.set(element, animStart);
    const tween = gsap.to(element, {
        ...animEnd,
        onComplete: () => {
            element.classList.add("animation-complete");
        }
    });

    return tween;
}

export function animateElements(elements, stagger = 0.06) {
    if (!elements) return [];

    const list = elements instanceof NodeList || Array.isArray(elements)
        ? Array.from(elements)
        : [elements];

    return list.map((el, index) => toggleCommonAnimation(el, index * stagger)).filter(Boolean);
}
